import { useState, useMemo, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import VideoPlayer from './VideoPlayer';
import PostMusicPlayer, { SongItem } from './PostMusicPlayer';
import LazyImage from '../LazyImage';
import Advertisement from './Advertisement';
import { useInView } from 'react-intersection-observer';
import { adService } from '../../lib/supabase-service';

export interface MediaItem {
  id: string;
  url: string;
  type: 'image' | 'video' | 'music' | 'ad';
  thumbnail?: string;
  title?: string;
  artist?: string;
  duration?: number;
  width?: number;
  height?: number;
  orientation?: 'landscape' | 'portrait' | 'square';
  is_ad?: boolean;
}

interface MediaViewerProps {
  media_url: string;
  caption?: string;
  mediaType?: 'image' | 'video' | 'music';
  className?: string;
  onMediaView?: (mediaId: string) => void;
  onMediaClick?: (mediaIndex: number) => void;
  additionalMediaUrls?: (string | MediaItem)[];
  musicInfo?: {
    title: string;
    artist: string;
    coverArt: string;
    duration: number;
  };
  initialMediaIndex?: number;
  postId?: string;
  postOwnerId?: string;
}

/**
 * Enhanced media type detection with support for various URL patterns and fallbacks
 * @param url - The media URL to analyze
 * @param fallbackType - Default type if detection fails
 * @returns Detected media type
 */
function detectMediaType(url: string, fallbackType: 'image' | 'video' | 'music' = 'image'): 'image' | 'video' | 'music' {
  if (!url || typeof url !== 'string') return fallbackType;
  
  const urlLower = url.toLowerCase();
  
  // Video extensions and patterns
  const videoPatterns = [
    /\.(mp4|mov|avi|webm|mkv|flv|wmv|m4v|3gp|ogv)(\?.*)?$/i,
    /youtube\.com\/watch/i,
    /youtu\.be\//i,
    /vimeo\.com\//i,
    /dailymotion\.com\//i,
    /twitch\.tv\//i
  ];
  
  // Audio/Music extensions and patterns
  const musicPatterns = [
    /\.(mp3|wav|ogg|aac|flac|m4a|wma|opus|webm)(\?.*)?$/i,
    /spotify\.com\//i,
    /soundcloud\.com\//i,
    /apple\.com\/music/i
  ];
  
  // Image extensions and patterns
  const imagePatterns = [
    /\.(jpg|jpeg|png|gif|webp|svg|bmp|tiff|ico|avif)(\?.*)?$/i,
    /unsplash\.com\//i,
    /pixabay\.com\/.*\/photo/i,
    /pexels\.com\//i
  ];
  
  // Check video patterns first
  if (videoPatterns.some(pattern => pattern.test(urlLower))) {
    return 'video';
  }
  
  // Check music patterns
  if (musicPatterns.some(pattern => pattern.test(urlLower))) {
    return 'music';
  }
  
  // Check image patterns
  if (imagePatterns.some(pattern => pattern.test(urlLower))) {
    return 'image';
  }
  
  // Additional heuristics based on URL structure
  if (urlLower.includes('video') || urlLower.includes('stream')) {
    return 'video';
  }
  
  if (urlLower.includes('audio') || urlLower.includes('music') || urlLower.includes('sound')) {
    return 'music';
  }
  
  // Default fallback
  return fallbackType;
}

/**
 * MediaViewer component for displaying various media types (images, videos, music)
 * Supports single media items and carousels with multiple media items
 * Handles responsive sizing and proper display across different PostView sizes
 */
export default function MediaViewer({
  media_url,
  caption = '',
  mediaType = 'image',
  className = '',
  onMediaView,
  onMediaClick,
  additionalMediaUrls = [],
  musicInfo,
  initialMediaIndex = 0,
  postId,
  postOwnerId
}: MediaViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(initialMediaIndex);
  const [imageDimensions, setImageDimensions] = useState<{[key: string]: {width: number, height: number}}>({});
  
  // Use intersection observer to detect when media is in viewport
  const { ref: viewRef, inView } = useInView({
    threshold: 0.3,
    triggerOnce: false
  });

  // Process media items into a standardized format
  const mediaItems = useMemo(() => {
    const items: MediaItem[] = [];
    
    // Add primary media URL only if it exists and is not empty
    if (media_url && typeof media_url === 'string' && media_url.trim() !== '') {
      items.push({
        id: '1',
        url: media_url,
        type: detectMediaType(media_url, mediaType),
        thumbnail: mediaType === 'music' ? musicInfo?.coverArt : media_url,
        title: mediaType === 'music' ? musicInfo?.title : caption,
        artist: mediaType === 'music' ? musicInfo?.artist : undefined,
        duration: mediaType === 'music' ? musicInfo?.duration : undefined
      });
    }
    
    // Add any additional media URLs (all assumed to be images unless specified)
    additionalMediaUrls.forEach((item, index) => {
      // Only add if URL exists and is not empty
      if (typeof item === 'string' && item.trim() !== '') {
        // Determine media type from URL extension
        const type: 'image' | 'video' | 'music' = detectMediaType(item);
        
        items.push({
          id: `additional-${index + 2}`,
          url: item,
          type,
          thumbnail: type === 'video' ? item : undefined
        });
      } else if (typeof item === 'object' && item !== null) {
        items.push(item);
      }
    });
    
    // Add advertisement on slide 2 ONLY for multi-media posts (more than 1 media item)
    const hasMultipleMedia = items.length > 1;
    if (postId && postOwnerId && hasMultipleMedia) {
      items.splice(1, 0, {
        id: 'ad-1',
        url: '',
        type: 'ad',
        thumbnail: '',
        title: '',
        artist: '',
        duration: undefined,
        is_ad: true
      });
    }
    
    return items;
  }, [media_url, mediaType, additionalMediaUrls, musicInfo, caption, postId, postOwnerId]);

  // Skip if no valid media items
  if (mediaItems.length === 0) {
    return null;
  }
  
  const currentMedia = mediaItems[currentIndex];
  const isSingleItem = mediaItems.length === 1;
  
  // Handle image load to detect dimensions
  const handleImageLoad = (url: string, img: HTMLImageElement) => {
    setImageDimensions(prev => ({
      ...prev,
      [url]: {
        width: img.naturalWidth,
        height: img.naturalHeight
      }
    }));
    
    if (onMediaView) {
      onMediaView(currentMedia.id);
    }
  };
  
  // Get image orientation
  const getImageOrientation = (url: string): 'portrait' | 'landscape' | 'square' => {
    const dims = imageDimensions[url];
    if (!dims) return 'square';
    
    const ratio = dims.width / dims.height;
    if (ratio > 1.1) return 'landscape';
    if (ratio < 0.9) return 'portrait';
    return 'square';
  };
  
  const handlePrevious = () => {
    const newIndex = currentIndex === 0 ? mediaItems.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    
    // Trigger media view for the new slide
    if (onMediaView && mediaItems[newIndex]) {
      onMediaView(mediaItems[newIndex].id);
    }
    
    // Track ad view if navigating to an ad
    if (mediaItems[newIndex]?.is_ad && postId && postOwnerId) {
      adService.trackAdView(postId, 'slide_ad', newIndex);
    }
  };
  
  const handleNext = () => {
    const newIndex = currentIndex === mediaItems.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    
    // Trigger media view for the new slide
    if (onMediaView && mediaItems[newIndex]) {
      onMediaView(mediaItems[newIndex].id);
    }
    
    // Track ad view if navigating to an ad
    if (mediaItems[newIndex]?.is_ad && postId && postOwnerId) {
      adService.trackAdView(postId, 'slide_ad', newIndex);
    }
  };

  // Prepare music player data if needed
  let songItem: SongItem | null = null;
  if (currentMedia.type === 'music') {
    songItem = {
      id: currentMedia.id,
      url: currentMedia.url,
      title: currentMedia.title || 'Unknown Track',
      artist: currentMedia.artist || 'Unknown Artist',
      coverArt: currentMedia.thumbnail || 'https://cdn.pixabay.com/photo/2018/06/30/09/29/music-3507317_1280.jpg',
      duration: typeof currentMedia.duration === 'number' ? currentMedia.duration : 180 // Default to 3 minutes
    };
  }

  useEffect(() => {
    if (currentMedia.is_ad && postId && postOwnerId) {
      adService.trackAdView(postId, 'slide_ad', currentIndex);
    }
  }, [currentMedia, postId, postOwnerId, currentIndex]);

  return (
    <div 
      ref={viewRef}
      className={`relative w-full h-full overflow-hidden ${className}`}
    >
      {/* Media Content */}
      {currentMedia.type === 'image' ? (
        <div 
          className={`bg-black flex-1 flex items-center justify-center w-full h-full ${isSingleItem ? 'cursor-pointer' : ''}`}
          onClick={isSingleItem ? () => onMediaClick && onMediaClick(currentIndex) : undefined}
        >
          <LazyImage
            src={currentMedia.url}
            alt={caption || 'Media content'}
            className={`${
              isSingleItem && getImageOrientation(currentMedia.url) === 'portrait' 
                ? 'h-full w-auto object-contain' 
                : getImageOrientation(currentMedia.url) === 'landscape'
                ? 'w-full h-auto object-contain max-h-full'
                : 'w-full h-full object-cover'
            }`}
            onLoad={(img) => handleImageLoad(currentMedia.url, img)}
          />
        </div>
      ) : currentMedia.type === 'video' ? (
        <div 
          className={`bg-black flex-1 flex items-center justify-center w-full h-full ${isSingleItem ? 'cursor-pointer' : ''}`}
          onClick={isSingleItem ? () => onMediaClick && onMediaClick(currentIndex) : undefined}
        >
          <VideoPlayer
            src={currentMedia.url}
            poster={currentMedia.thumbnail}
            className="w-full h-full object-contain block !mb-0 !pb-0 z-10"
          />
        </div>
      ) : currentMedia.type === 'music' && songItem ? (
        <div 
          className={`w-full h-full flex items-center justify-center ${isSingleItem ? 'cursor-pointer' : ''}`}
          onClick={isSingleItem ? () => onMediaClick && onMediaClick(currentIndex) : undefined}
        >
          <PostMusicPlayer 
            song={songItem} 
            autoPlay={inView}
            hasMultipleSongs={mediaItems.length > 1}
            onNext={mediaItems.length > 1 ? handleNext : undefined}
            onPrevious={mediaItems.length > 1 ? handlePrevious : undefined}
            className="w-full h-full !mb-0 !pb-0"
          />
        </div>
      ) : currentMedia.type === 'ad' ? (
        <div 
          className={`w-full h-full flex items-center justify-center ${isSingleItem ? 'cursor-pointer' : ''}`}
          onClick={isSingleItem ? () => onMediaClick && onMediaClick(currentIndex) : undefined}
        >
          <Advertisement 
            postId={postId} 
            postOwnerId={postOwnerId} 
            className="w-full h-full !mb-0 !pb-0"
          />
        </div>
      ) : null}
      
      {/* Navigation Arrows and Indicators (only show if multiple items) */}
      {!isSingleItem && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePrevious();
            }}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-[#065900] text-white p-2 rounded-full transition-colors shadow-lg z-20"
            aria-label="Previous"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-[#065900] text-white p-2 rounded-full transition-colors shadow-lg z-20"
            aria-label="Next"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
          
          {/* Slide Number Indicator */}
          <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
            {currentIndex + 1}/{mediaItems.length}
          </div>
          
          {/* Dots Indicator */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1">
            {mediaItems.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentIndex(index);
                }}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  index === currentIndex 
                    ? 'bg-white' 
                    : 'bg-white/50 hover:bg-white/75'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
